import { CustomerAddressElementDataType } from '@/components/sections/builder/elements/body/customer/customer_address/type';
import { ElementProps } from '@/components/sections/builder/types';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { deleteCustomerAddress, setDefaultCustomerAddress } from '@/lib/actions/customer/action';
import { debounce } from '@/lib/utils';
import { BaseCustomerAddress } from '@/types/http/customer/common';
import { MapPinned, Plus, Search } from 'lucide-react';
import React from 'react';
import Cookies from 'js-cookie';
import { queryClient } from '@/lib/query-client';
import { toast } from '@/components/ui/use-toast';
import { dummyCustomerAddress } from '@/constant/dummyData/dummy-customer-address';
import { ModalCustomerAddress } from '@/components/sections/builder/elements/body/customer/customer_address/form/modal-customer-address';
import useModal from '@/hooks/useModal';
import CardAddress from '@/components/sections/builder/elements/body/customer/customer_address/components/card-address';
import { HttpTypes } from '@/types';

export default function ELementContentCustomerAddress({
  element,
  host,
  data,
  preview,
}: ElementProps) {
  const content = element.data as CustomerAddressElementDataType;
  const token = Cookies.get('accessToken');
  const modalCustomer = useModal<{
    mode: 'add' | 'edit';
    data?: BaseCustomerAddress;
  }>();

  const [isLoading, setIsLoading] = React.useState<{ [key: string]: boolean }>({});

  const addresses = React.useMemo(
    () => (preview ? dummyCustomerAddress : Array.isArray(data) ? data : []),
    [preview, data]
  );

  const defaultAddress = addresses.find((address) => address.is_default);
  const notDefaultAddress = addresses.filter((address) => !address.is_default);

  const handleDefaultAddress = React.useMemo(
    () =>
      debounce(async (id: string) => {
        try {
          setIsLoading((prev) => ({ ...prev, [id]: true }));
          const res = await setDefaultCustomerAddress(host as string, token as string, id);

          if (res.success) {
            queryClient.invalidateQueries({ queryKey: ['customer_list_address'] });
            toast({
              title: 'Success',
              variant: 'success',
              description: res.message || 'Address has been successfully set as default',
            });
          } else {
            toast({
              title: 'Error',
              variant: 'destructive',
              description: res.message || 'Something went wrong',
            });
          }
        } catch (error) {
          if (error instanceof Error) {
            toast({
              title: 'Error',
              description: error.message || 'Something went wrong',
              variant: 'destructive',
            });
          }
        } finally {
          setIsLoading((prev) => ({ ...prev, [id]: false }));
        }
      }, 500),
    [host, token]
  );

  const handleDeleteAddress = React.useMemo(
    () =>
      debounce(async (id: string) => {
        try {
          setIsLoading((prev) => ({ ...prev, [id]: true }));
          const res = await deleteCustomerAddress(host as string, token as string, id);

          if (res.success) {
            queryClient.invalidateQueries({ queryKey: ['customer_list_address'] });
            toast({
              title: 'Success',
              variant: 'success',
              description: 'Address has been successfully deleted',
            });
          } else {
            toast({
              title: 'Error',
              variant: 'destructive',
              description: res.message || 'Something went wrong',
            });
          }
        } catch (error) {
          if (error instanceof Error) {
            toast({
              title: 'Error',
              description: error.message || 'Something went wrong',
              variant: 'destructive',
            });
          }
        } finally {
          setIsLoading((prev) => ({ ...prev, [id]: false }));
        }
      }, 500),
    [host, token]
  );

  return (
    <>
      <div className='bg_scheme_customer_detail customer_detail_padding mx-auto min-h-[calc(100vh_-_300px)] w-full md:container'>
        <div className='theme_layout_page_width mx-auto flex w-full flex-col gap-3 px-4'>
          <div className='flex items-center justify-between'>
            <h1 className='text-xl font-bold'>My Address</h1>
            <Button
              className=''
              style={{
                backgroundColor: content.colorScheme?.btnSolidBackgroud,
                color: content.colorScheme?.btnSolidlabel,
              }}
              onClick={() =>
                modalCustomer.open({
                  mode: 'add',
                })
              }
              data-testid='add-address-customer-storefront'
              disabled={preview}
            >
              <Plus className='mr-2 h-4 w-4' />
              Add New Address
            </Button>
          </div>
          <Input
            className=''
            placeholder='Enter address / city / district destination'
            leftContent={<Search className='ml-4 h-4 w-4' />}
            type='search'
          />
          <div className='space-y-4'>
            {defaultAddress && (
              <CardAddress
                address={defaultAddress}
                content={content}
                isDefault
                isLoading={
                  defaultAddress && defaultAddress.id ? isLoading[defaultAddress.id] : false
                }
                modalCustomer={modalCustomer}
              />
            )}
            {notDefaultAddress.map((item, index) => (
              <CardAddress
                key={index}
                address={item}
                content={content}
                isLoading={item.id ? isLoading[item.id] : false}
                modalCustomer={modalCustomer}
                handleDefaultAddress={handleDefaultAddress}
                handleDeleteAddress={handleDeleteAddress}
              />
            ))}

            {addresses.length === 0 && (
              <Card>
                <CardContent className='flex flex-col items-center justify-center py-10 text-center'>
                  <div className='mb-4 rounded-full bg-muted p-3'>
                    <MapPinned className='h-6 w-6' />
                  </div>
                  <h3 className='mb-1 text-lg font-semibold'>No address added</h3>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      <ModalCustomerAddress
        initialValues={modalCustomer.data?.data as unknown as HttpTypes.StoreCustomerAddressPayload}
        openModal={modalCustomer.isShow}
        setOpenModal={modalCustomer.setIsShow}
        mode={modalCustomer.data?.mode}
        host={host}
        token={token}
        content={content}
      />
      <style jsx>
        {`
          .bg_scheme_customer_detail {
            background-color: ${content.colorScheme?.background || 'white'};
            color: ${content.colorScheme?.text || '#52535B'};
          }
          .customer_detail_padding {
            padding-top: ${content.topPadding}rem;
            padding-bottom: ${content.bottomPadding}rem;
          }
        `}
      </style>
    </>
  );
}
